// contexts/ModuleContext.tsx
import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { moduleService } from '../services/moduleService';
import { BaseModule, EntrepriseModule, Module } from '../types/features/Module';

// ============================================
// TYPES
// ============================================
interface ModuleContextType {
  // Données
  modules: Module[];
  entrepriseModules: EntrepriseModule[];
  selectedModule: Module | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    lastPage: number;
    total: number;
    perPage: number;
  };

  // Méthodes publiques
  getModules: (page?: number, perPage?: number, search?: string, is_active?: boolean) => Promise<void>;
  getModuleById: (id: string) => Promise<Module | null>;

  // Méthodes entreprise
  getEntrepriseModules: () => Promise<EntrepriseModule[] | null>;
  activateModule: (moduleId: string) => Promise<boolean>;
  deactivateModule: (moduleId: string) => Promise<boolean>;
  toggleModule: (moduleId: string) => Promise<boolean>;

  // Méthodes admin
  createModule: (data: BaseModule) => Promise<Module | null>;
  updateModule: (id: string, data: Partial<Module>) => Promise<Module | null>;
  deleteModule: (id: string) => Promise<boolean>;
  toggleStatus: (id: string) => Promise<Module | null>;
}

// ============================================
// CONTEXT
// ============================================
export const ModuleContext = createContext<ModuleContextType | null>(null);

// ============================================
// PROVIDER
// ============================================
export const ModuleProvider = ({ children }: { children: ReactNode }) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [entrepriseModules, setEntrepriseModules] = useState<EntrepriseModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // ============================================
  // ROUTES PUBLIQUES
  // ============================================

  const getModules = useCallback(async (page: number = 1, perPage: number = 20, search?: string, is_active?: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const response = await moduleService.getModules(page, perPage, search, is_active);
      setModules(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des modules");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getModuleById = useCallback(async (id: string): Promise<Module | null> => {
    setLoading(true);
    setError(null);
    try {
      const module = await moduleService.getModuleById(id);
      setSelectedModule(module);
      return module;
    } catch (err) {
      setError("Erreur lors du chargement du module");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // ROUTES ENTREPRISE
  // ============================================

  const getEntrepriseModules = useCallback(async (): Promise<EntrepriseModule[] | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await moduleService.getEntrepriseModules();
      setEntrepriseModules(response.data);
      return response.data;
    } catch (err: any) {
      setError("Erreur lors du chargement des modules de l'entreprise");
      console.error(err.response);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const activateModule = useCallback(async (moduleId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const result = await moduleService.activateModule(moduleId);
      setEntrepriseModules((prev) =>
        prev.map((m) =>
          m.id === moduleId
            ? { ...m, is_enabled: true, activated_at: result.activated_at }
            : m
        )
      );
      return true;
    } catch (err: any) {
      setError("Erreur lors de l'activation du module");
      console.error(err.response);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const deactivateModule = useCallback(async (moduleId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await moduleService.deactivateModule(moduleId);
      setEntrepriseModules((prev) =>
        prev.map((m) =>
          m.id === moduleId
            ? { ...m, is_enabled: false, activated_at: null }
            : m
        )
      );
      return true;
    } catch (err) {
      setError("Erreur lors de la désactivation du module");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleModule = useCallback(async (moduleId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const result = await moduleService.toggleModule(moduleId);
      setEntrepriseModules((prev) =>
        prev.map((m) =>
          m.id === moduleId
            ? { ...m, is_enabled: result.is_active, activated_at: result.activated_at }
            : m
        )
      );
      return true;
    } catch (err) {
      setError("Erreur lors du basculement du module");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // ROUTES ADMIN
  // ============================================

  const createModule = useCallback(async (data: BaseModule): Promise<Module | null> => {
    setLoading(true);
    setError(null);
    try {
      const newModule = await moduleService.createModule(data);
      setModules((prev) => [newModule, ...prev]);
      return newModule;
    } catch (err) {
      setError("Erreur lors de la création du module");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateModule = useCallback(async (id: string, data: Partial<Module>): Promise<Module | null> => {
    setLoading(true);
    setError(null);
    try {
      const updatedModule = await moduleService.updateModule(id, data);
      setModules((prev) => prev.map((e) => (e.id === id ? updatedModule : e)));
      if (selectedModule?.id === id) setSelectedModule(updatedModule);
      return updatedModule;
    } catch (err: any) {
      setError("Erreur lors de la mise à jour du module");
      console.error(err?.response?.data);
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedModule]);

  const deleteModule = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await moduleService.deleteModule(id);
      setModules((prev) => prev.filter((e) => e.id !== id));
      if (selectedModule?.id === id) setSelectedModule(null);
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression du module");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [selectedModule]);

  const toggleStatus = useCallback(async (id: string): Promise<Module | null> => {
    setLoading(true);
    setError(null);
    try {
      const updatedModule = await moduleService.toggleStatus(id);
      setModules((prev) => prev.map((e) => 
        e.id === id ? updatedModule : e
      ));
      if (selectedModule?.id === id) setSelectedModule(updatedModule);
      return updatedModule;
    } catch (err) {
      setError("Erreur lors du changement de statut du module");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedModule]);

  // ============================================
  // INIT
  // ============================================
  useEffect(() => {
    getModules();
    getEntrepriseModules();
  }, []);

  // ============================================
  // VALUE
  // ============================================
  const value: ModuleContextType = {
    modules,
    entrepriseModules,
    selectedModule,
    loading,
    error,
    pagination,
    getModules,
    getModuleById,
    getEntrepriseModules,
    activateModule,
    deactivateModule,
    toggleModule,
    createModule,
    updateModule,
    deleteModule,
    toggleStatus,
  };

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
};
